import React, { useState } from 'react';

interface WelcomeGuideProps {
  onClose: () => void;
}

type Screen = 'home' | 'discover' | 'journal' | 'myscent' | 'follow' | 'concierge';

// ─── Tiny reusable SVG wrapper ───────────────────────────────────────────────
const Ill = ({ children, viewBox, h = 88 }: { children: React.ReactNode; viewBox: string; h?: number }) => (
  <svg viewBox={viewBox} xmlns="http://www.w3.org/2000/svg"
    style={{ width: '100%', height: h, display: 'block', borderRadius: 10 }}>
    {children}
  </svg>
);

// ─── Accent colours per tab ───────────────────────────────────────────────────
const C = {
  discover: '#C4A882',
  journal:  '#8CA87A',
  myscent:  '#A08CC4',
  follow:   '#89C4D4',
  concierge:'#2A2520',
};

// ─── Feature data ─────────────────────────────────────────────────────────────

const DISCOVER_FEATURES = [
  {
    color: C.discover,
    icon: '%',
    label: 'Your match score',
    blurb: 'Every card shows how well it fits your taste.',
    preview: (
      <Ill viewBox="0 0 320 96" h={96}>
        <rect width="320" height="96" fill="#F8F4EF" rx="10"/>
        {/* card */}
        <rect x="10" y="10" width="300" height="76" rx="9" fill="#fff" stroke="#C4A882" strokeWidth="1.8"/>
        {/* score ring */}
        <circle cx="46" cy="48" r="20" fill="none" stroke="#EDE8DF" strokeWidth="4"/>
        <circle cx="46" cy="48" r="20" fill="none" stroke="#C4A882" strokeWidth="4"
          strokeDasharray="74 100" strokeDashoffset="25" strokeLinecap="round"/>
        <text x="46" y="53" textAnchor="middle" fontFamily="system-ui" fontSize="9" fontWeight="700" fill="#2A2520">87%</text>
        <text x="46" y="64" textAnchor="middle" fontFamily="system-ui" fontSize="6.5" fill="#A09890" letterSpacing="0.08em">MATCH</text>
        {/* mini map */}
        <line x1="84" y1="28" x2="84" y2="68" stroke="#D4CEBF" strokeWidth="0.8"/>
        <line x1="64" y1="48" x2="104" y2="48" stroke="#D4CEBF" strokeWidth="0.8"/>
        <circle cx="89" cy="41" r="8" fill="#C4A882" fillOpacity="0.2"/>
        <circle cx="89" cy="41" r="4.5" fill="#C4A882" fillOpacity="0.6"/>
        <circle cx="89" cy="41" r="2" fill="#C4A882"/>
        {/* name */}
        <text x="116" y="38" fontFamily="Georgia,serif" fontSize="13" fill="#2A2520">Baccarat Rouge 540</text>
        <text x="116" y="52" fontFamily="system-ui" fontSize="9.5" fill="#A09890">Maison Francis Kurkdjian</text>
        <rect x="116" y="58" width="36" height="14" rx="7" fill="#D4A0A8" fillOpacity="0.3" stroke="#D4A0A8" strokeWidth="1"/>
        <text x="134" y="68.5" fontFamily="system-ui" fontSize="7.5" fill="#A05060" textAnchor="middle">Bloom</text>
        {/* % badge highlight */}
        <rect x="10" y="10" width="72" height="76" rx="9" fill="#FFF8F0" fillOpacity="0.7"/>
        <text x="46" y="86" textAnchor="middle" fontFamily="system-ui" fontSize="8" fill="#C4A882">← your fit score</text>
      </Ill>
    ),
  },
  {
    color: '#2A2520',
    icon: '♥',
    label: 'Heart = new session',
    blurb: 'Always opens a fresh wear log — every time.',
    preview: (
      <Ill viewBox="0 0 320 80" h={80}>
        <rect width="320" height="80" fill="#F8F4EF" rx="10"/>
        <rect x="10" y="10" width="300" height="60" rx="9" fill="#fff" stroke="#EDE8DF" strokeWidth="1.5"/>
        {/* buttons right */}
        <circle cx="258" cy="40" r="15" fill="#F5F0E8" stroke="#EDE8DF" strokeWidth="1.2"/>
        <path d="M258 35a4 4 0 0 0-5.6 5.7L258 47l5.6-6.3A4 4 0 0 0 258 35z" fill="#2A2520"/>
        <circle cx="285" cy="40" r="15" fill="#F5F0E8" stroke="#EDE8DF" strokeWidth="1.2"/>
        <line x1="285" y1="34" x2="285" y2="46" stroke="#A09890" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="279" y1="40" x2="291" y2="40" stroke="#A09890" strokeWidth="1.5" strokeLinecap="round"/>
        {/* annotation */}
        <rect x="140" y="28" width="98" height="24" rx="12" fill="#2A2520"/>
        <text x="189" y="44" fontFamily="system-ui" fontSize="9" fill="#F5F0E8" textAnchor="middle">Log new session</text>
        <line x1="230" y1="40" x2="243" y2="40" stroke="#2A2520" strokeWidth="1.2" markerEnd="url(#arr)"/>
        <text x="50" y="38" fontFamily="Georgia,serif" fontSize="13" fill="#2A2520">Changing Constance</text>
        <text x="50" y="52" fontFamily="system-ui" fontSize="9" fill="#A09890">Penhaligon's</text>
      </Ill>
    ),
  },
  {
    color: '#2A2520',
    icon: 'J',
    label: '"Journaled" → view your notes',
    blurb: 'Tap the black tag to read your sessions for that scent.',
    preview: (
      <Ill viewBox="0 0 320 108" h={108}>
        <rect width="320" height="108" fill="#F8F4EF" rx="10"/>
        {/* card */}
        <rect x="10" y="10" width="300" height="50" rx="9" fill="#fff" stroke="#EDE8DF" strokeWidth="1.5"/>
        <text x="56" y="30" fontFamily="Georgia,serif" fontSize="13" fill="#2A2520">Changing Constance</text>
        <text x="56" y="44" fontFamily="system-ui" fontSize="9" fill="#A09890">Penhaligon's</text>
        {/* Journaled tag */}
        <rect x="130" y="46" width="74" height="16" rx="5" fill="#2A2520"/>
        <text x="167" y="57.5" fontFamily="system-ui" fontSize="8" fill="#F5F0E8" textAnchor="middle" letterSpacing="0.08em">JOURNALED</text>
        <text x="130" y="74" fontFamily="system-ui" fontSize="9" fill="#2A2520">↑ tap to open</text>
        {/* popup */}
        <rect x="10" y="82" width="300" height="22" rx="7" fill="#2A2520" fillOpacity="0.06" stroke="#EDE8DF" strokeWidth="1"/>
        <circle cx="30" cy="93" r="4" fill="#8CA87A"/>
        <text x="40" y="97" fontFamily="system-ui" fontSize="9" fill="#2A2520" fontWeight="600">ATF</text>
        <text x="88" y="97" fontFamily="system-ui" fontSize="9" fill="#A09890">· 3 sessions · last Jun 2</text>
      </Ill>
    ),
  },
  {
    color: C.discover,
    icon: '◈',
    label: 'Scent categories',
    blurb: 'Air · Bloom · Terrain · Noir · Velvet — from the notes.',
    preview: (
      <Ill viewBox="0 0 320 52" h={52}>
        <rect width="320" height="52" fill="#F8F4EF" rx="10"/>
        {[
          { x: 8,   label: 'All',     bg: '#2A2520', tc: '#F5F0E8', w: 30 },
          { x: 44,  label: 'Air',     bg: '#89C4D4', tc: '#2A7A94', w: 34 },
          { x: 84,  label: 'Bloom',   bg: '#D4A0A8', tc: '#A05060', w: 44 },
          { x: 134, label: 'Terrain', bg: '#8CA87A', tc: '#3A6028', w: 54 },
          { x: 194, label: 'Noir',    bg: '#5A5460', tc: '#FAF7FF', w: 38 },
          { x: 238, label: 'Velvet',  bg: '#C4A882', tc: '#6A4820', w: 46 },
        ].map(({ x, label, bg, tc, w }) => (
          <g key={label}>
            <rect x={x} y="14" width={w} height="22" rx="11" fill={bg} fillOpacity={bg === '#2A2520' ? 1 : 0.28} stroke={bg} strokeWidth="1"/>
            <text x={x + w / 2} y="28.5" fontFamily="system-ui" fontSize="8.5" fill={tc} textAnchor="middle">{label}</text>
          </g>
        ))}
      </Ill>
    ),
  },
  {
    color: '#8CA87A',
    icon: '+',
    label: 'Not seeing it? Add it.',
    blurb: 'Concierge looks it up, adds it, and credits you.',
    preview: (
      <Ill viewBox="0 0 320 64" h={64}>
        <rect width="320" height="64" fill="#F8F4EF" rx="10"/>
        <rect x="12" y="12" width="210" height="32" rx="8" fill="#fff" stroke="#EDE8DF" strokeWidth="1.5"/>
        <text x="24" y="32" fontFamily="system-ui" fontSize="12" fill="#2A2520">Acqua di Gioia</text>
        <rect x="228" y="12" width="80" height="32" rx="8" fill="#2A2520"/>
        <text x="268" y="32" fontFamily="system-ui" fontSize="9.5" fill="#F5F0E8" textAnchor="middle" letterSpacing="1">FIND</text>
        <circle cx="22" cy="54" r="4" fill="#8CA87A"/>
        <text x="32" y="57.5" fontFamily="system-ui" fontSize="9" fill="#5A7A48">Found · added to catalog · +1 contribution</text>
      </Ill>
    ),
  },
];

const JOURNAL_FEATURES = [
  {
    color: C.journal,
    icon: '★',
    label: 'Rate every wear',
    blurb: 'ATF · Like · Neutral · Dislike — rated per session, not per scent.',
    preview: (
      <Ill viewBox="0 0 320 80" h={80}>
        <rect width="320" height="80" fill="#F8F4EF" rx="10"/>
        {[
          { x: 12,  label: 'ATF',     active: true,  color: '#8CA87A' },
          { x: 88,  label: 'LIKE',    active: false, color: '#C4A882' },
          { x: 152, label: 'NEUTRAL', active: false, color: '#A09890' },
          { x: 232, label: 'DISLIKE', active: false, color: '#C4697A' },
        ].map(({ x, label, active, color }) => (
          <g key={label}>
            <rect x={x} y="22" width={label === 'NEUTRAL' || label === 'DISLIKE' ? 68 : 64} height="36" rx="10"
              fill={active ? color : '#fff'}
              stroke={active ? color : '#EDE8DF'}
              strokeWidth={active ? 2 : 1.2}/>
            <text x={x + (label === 'NEUTRAL' || label === 'DISLIKE' ? 34 : 32)} y="44"
              fontFamily="system-ui" fontSize="9" fontWeight={active ? '700' : '400'}
              fill={active ? '#fff' : '#A09890'} textAnchor="middle">{label}</text>
          </g>
        ))}
      </Ill>
    ),
  },
  {
    color: C.journal,
    icon: '✎',
    label: 'Capture the drydown',
    blurb: 'First impression · after an hour · end of day.',
    preview: (
      <Ill viewBox="0 0 320 96" h={96}>
        <rect width="320" height="96" fill="#F8F4EF" rx="10"/>
        {[
          { y: 12, label: 'FIRST IMPRESSION', text: 'Warm and soft, something skin-like', color: '#C4A882' },
          { y: 44, label: 'AFTER AN HOUR',    text: 'The amber and vanilla bloom', color: '#8CA87A' },
          { y: 72, label: 'END OF DAY',        text: 'Still lingering beautifully at 10pm', color: '#89C4D4' },
        ].map(({ y, label, text, color }) => (
          <g key={label}>
            <rect x="12" y={y} width="8" height="18" rx="4" fill={color}/>
            <text x="26" y={y + 9} fontFamily="system-ui" fontSize="7.5" fill={color} letterSpacing="0.1em">{label}</text>
            <text x="26" y={y + 20} fontFamily="system-ui" fontSize="9.5" fill="#2A2520">{text}</text>
          </g>
        ))}
      </Ill>
    ),
  },
  {
    color: '#A09890',
    icon: '⊞',
    label: 'Ownership',
    blurb: "Sample · Bottle · Would buy again · No longer own. Nothing = you tried it somewhere.",
    preview: (
      <Ill viewBox="0 0 320 72" h={72}>
        <rect width="320" height="72" fill="#F8F4EF" rx="10"/>
        <text x="14" y="20" fontFamily="system-ui" fontSize="8" fill="#A09890" letterSpacing="0.12em">OWNERSHIP · SELECT ALL THAT APPLY</text>
        {[
          { x: 14,  label: 'Sample',          checked: true  },
          { x: 100, label: 'Bottle',           checked: false },
          { x: 174, label: 'Would buy again',  checked: false },
          { x: 14,  label: 'No longer own',    y2: true, checked: false },
        ].map(({ x, label, checked, y2 }, i) => (
          <g key={label}>
            <rect x={x} y={y2 ? 48 : 28} width={label.length * 6.2 + 28} height="20" rx="6"
              fill={checked ? '#2A2520' : '#fff'} stroke={checked ? '#2A2520' : '#EDE8DF'} strokeWidth="1.2"/>
            <text x={x + 12} y={y2 ? 61.5 : 41.5} fontFamily="system-ui" fontSize="9" fill={checked ? '#F5F0E8' : '#8A7E78'}>{label}</text>
          </g>
        ))}
        <text x="14" y="88" fontFamily="system-ui" fontSize="8" fill="#A09890"/>
      </Ill>
    ),
  },
  {
    color: '#A09890',
    icon: '≡',
    label: 'Filter & sort',
    blurb: 'By rating, season, time of day, or ownership.',
    preview: (
      <Ill viewBox="0 0 320 48" h={48}>
        <rect width="320" height="48" fill="#F8F4EF" rx="10"/>
        {[
          { x: 8,   label: 'All',    active: true  },
          { x: 44,  label: '★ Fave', active: false },
          { x: 102, label: '♥ Like', active: false },
          { x: 158, label: '~ Meh',  active: false },
          { x: 208, label: '✕ No',   active: false },
        ].map(({ x, label, active }) => (
          <g key={label}>
            <rect x={x} y="12" width={label.length * 6.5 + 16} height="22" rx="11"
              fill={active ? '#2A2520' : '#fff'} stroke={active ? '#2A2520' : '#EDE8DF'} strokeWidth="1.2"/>
            <text x={x + (label.length * 6.5 + 16) / 2} y="26.5" fontFamily="system-ui" fontSize="9"
              fill={active ? '#F5F0E8' : '#8A7E78'} textAnchor="middle">{label}</text>
          </g>
        ))}
      </Ill>
    ),
  },
];

const MYSCENT_FEATURES = [
  {
    color: C.myscent,
    icon: 'id',
    label: 'Your Scent Identity',
    blurb: 'Archetype · profile · 3 recommendations · a challenge.',
    preview: (
      <Ill viewBox="0 0 320 100" h={100}>
        <rect width="320" height="100" fill="#F8F4EF" rx="10"/>
        {/* archetype card */}
        <rect x="10" y="10" width="140" height="80" rx="9" fill="#fff" stroke="#A08CC4" strokeWidth="1.5"/>
        <text x="80" y="34" fontFamily="system-ui" fontSize="7.5" fill="#A09890" textAnchor="middle" letterSpacing="0.12em">YOUR ARCHETYPE</text>
        <text x="80" y="54" fontFamily="Georgia,serif" fontSize="14" fill="#2A2520" textAnchor="middle">The Maximalist</text>
        <text x="80" y="68" fontFamily="system-ui" fontSize="8.5" fill="#A09890" textAnchor="middle">Woods · Resins · Gourmands</text>
        <rect x="28" y="74" width="104" height="12" rx="6" fill="#A08CC4" fillOpacity="0.15"/>
        <text x="80" y="83" fontFamily="system-ui" fontSize="7.5" fill="#7A5AA0" textAnchor="middle">2 refreshes remaining</text>
        {/* recs */}
        <rect x="162" y="10" width="148" height="36" rx="8" fill="#fff" stroke="#EDE8DF" strokeWidth="1.2"/>
        <text x="172" y="27" fontFamily="Georgia,serif" fontSize="11" fill="#2A2520">Bois des Îles</text>
        <text x="172" y="39" fontFamily="system-ui" fontSize="8.5" fill="#A09890">Chanel</text>
        <rect x="270" y="16" width="34" height="16" rx="8" fill="#C4A882" fillOpacity="0.2"/>
        <text x="287" y="27" fontFamily="system-ui" fontSize="8" fill="#8B5A2A" textAnchor="middle">91%</text>
        <rect x="162" y="52" width="148" height="36" rx="8" fill="#fff" stroke="#EDE8DF" strokeWidth="1.2"/>
        <text x="172" y="69" fontFamily="Georgia,serif" fontSize="11" fill="#2A2520">Encens Mythique</text>
        <text x="172" y="81" fontFamily="system-ui" fontSize="8.5" fill="#A09890">Guerlain</text>
        <rect x="270" y="58" width="34" height="16" rx="8" fill="#C4A882" fillOpacity="0.2"/>
        <text x="287" y="69" fontFamily="system-ui" fontSize="8" fill="#8B5A2A" textAnchor="middle">88%</text>
      </Ill>
    ),
  },
  {
    color: '#C4A882',
    icon: '✦',
    label: 'Note to Self',
    blurb: 'Personal insights the AI noticed about your nose and skin.',
    preview: (
      <Ill viewBox="0 0 320 96" h={96}>
        <rect width="320" height="96" fill="#F8F4EF" rx="10"/>
        <text x="14" y="20" fontFamily="system-ui" fontSize="8" fill="#A09890" letterSpacing="0.12em">NOTE TO SELF</text>
        {[
          { y: 26, line1: 'Light florals fade fast on your skin —', line2: 'reach for resin-backed bases.', color: '#C4A882' },
          { y: 60, line1: 'You perceive base notes more than the heart —', line2: "you're smelling amber, not rose.", color: '#C4A882' },
        ].map(({ y, line1, line2, color }) => (
          <g key={y}>
            <rect x="12" y={y} width="4" height="28" rx="2" fill={color}/>
            <text x="24" y={y + 12} fontFamily="system-ui" fontSize="9.5" fill="#2A2520">{line1}</text>
            <text x="24" y={y + 24} fontFamily="system-ui" fontSize="9.5" fill="#2A2520">{line2}</text>
          </g>
        ))}
      </Ill>
    ),
  },
  {
    color: '#8CA87A',
    icon: '↕',
    label: 'Thumbs on recommendations',
    blurb: 'Your feedback trains the next profile refresh.',
    preview: (
      <Ill viewBox="0 0 320 72" h={72}>
        <rect width="320" height="72" fill="#F8F4EF" rx="10"/>
        <rect x="10" y="10" width="300" height="52" rx="9" fill="#fff" stroke="#EDE8DF" strokeWidth="1.2"/>
        <text x="22" y="30" fontFamily="Georgia,serif" fontSize="13" fill="#2A2520">Ambre Nuit</text>
        <text x="22" y="44" fontFamily="system-ui" fontSize="9" fill="#A09890">Christian Dior</text>
        <rect x="22" y="48" width="36" height="12" rx="6" fill="#C4A882" fillOpacity="0.2"/>
        <text x="40" y="57" fontFamily="system-ui" fontSize="7.5" fill="#8B5A2A" textAnchor="middle">89%</text>
        {/* thumbs */}
        <circle cx="248" cy="36" r="14" fill="#2A2520" stroke="#2A2520" strokeWidth="1.2"/>
        <text x="248" y="41" fontFamily="system-ui" fontSize="12" textAnchor="middle" fill="#F5F0E8">↑</text>
        <circle cx="282" cy="36" r="14" fill="#fff" stroke="#EDE8DF" strokeWidth="1.2"/>
        <text x="282" y="41" fontFamily="system-ui" fontSize="12" textAnchor="middle" fill="#A09890">↓</text>
        <text x="248" y="60" fontFamily="system-ui" fontSize="8" fill="#8CA87A" textAnchor="middle">sounds like me</text>
      </Ill>
    ),
  },
  {
    color: '#5A5460',
    icon: '⬡',
    label: 'Scent Tray',
    blurb: "Your owned bottles + today's pairing suggestion.",
    preview: (
      <Ill viewBox="0 0 320 88" h={88}>
        <rect width="320" height="88" fill="#F8F4EF" rx="10"/>
        <text x="14" y="18" fontFamily="system-ui" fontSize="8" fill="#A09890" letterSpacing="0.12em">TODAY'S PAIRING</text>
        {/* pair */}
        <rect x="12" y="24" width="144" height="28" rx="8" fill="#fff" stroke="#C4A882" strokeWidth="1.5"/>
        <circle cx="28" cy="38" r="6" fill="#C4A882" fillOpacity="0.5"/>
        <text x="40" y="42" fontFamily="Georgia,serif" fontSize="10" fill="#2A2520">Aventus</text>
        <text x="90" y="42" fontFamily="system-ui" fontSize="10" fill="#A09890">+</text>
        <circle cx="104" cy="38" r="6" fill="#89C4D4" fillOpacity="0.5"/>
        <text x="116" y="42" fontFamily="Georgia,serif" fontSize="10" fill="#2A2520">Sauvage</text>
        {/* tray grid */}
        <text x="14" y="66" fontFamily="system-ui" fontSize="8" fill="#A09890" letterSpacing="0.12em">YOUR TRAY</text>
        {['#C4A882','#8CA87A','#89C4D4','#A08CC4','#5A5460'].map((col, i) => (
          <rect key={i} x={12 + i * 30} y="72" width="24" height="12" rx="6" fill={col} fillOpacity="0.4"/>
        ))}
      </Ill>
    ),
  },
];

const FOLLOW_FEATURES = [
  {
    color: C.follow,
    icon: '#',
    label: 'Add by member number',
    blurb: 'Every member has a unique # — share yours to connect.',
    preview: (
      <Ill viewBox="0 0 320 68" h={68}>
        <rect width="320" height="68" fill="#F8F4EF" rx="10"/>
        <rect x="12" y="14" width="200" height="36" rx="9" fill="#fff" stroke="#EDE8DF" strokeWidth="1.2"/>
        <text x="24" y="37" fontFamily="system-ui" fontSize="13" fill="#2A2520"># 0004</text>
        <rect x="220" y="14" width="88" height="36" rx="9" fill="#2A2520"/>
        <text x="264" y="37" fontFamily="system-ui" fontSize="9.5" fill="#F5F0E8" textAnchor="middle" letterSpacing="1">FOLLOW</text>
        <text x="12" y="62" fontFamily="system-ui" fontSize="9" fill="#89C4D4">✓ Added Niki S. — Member #0004</text>
      </Ill>
    ),
  },
  {
    color: C.follow,
    icon: '⟡',
    label: 'Shared scent map',
    blurb: 'See how your taste clouds overlap — and where you diverge.',
    preview: (
      <Ill viewBox="0 0 320 96" h={96}>
        <rect width="320" height="96" fill="#F8F4EF" rx="10"/>
        <rect x="10" y="10" width="140" height="76" rx="9" fill="#fff" stroke="#EDE8DF" strokeWidth="1.2"/>
        <line x1="80" y1="14" x2="80" y2="82" stroke="#D4CEBF" strokeWidth="0.8"/>
        <line x1="14" y1="48" x2="146" y2="48" stroke="#D4CEBF" strokeWidth="0.8"/>
        {/* your cloud */}
        <circle cx="90" cy="38" r="10" fill="#C4A882" fillOpacity="0.25"/>
        <circle cx="95" cy="32" r="7" fill="#C4A882" fillOpacity="0.4"/>
        <circle cx="87" cy="44" r="6" fill="#C4A882" fillOpacity="0.35"/>
        {/* friend cloud */}
        <circle cx="84" cy="36" r="9" fill="#89C4D4" fillOpacity="0.25"/>
        <circle cx="88" cy="30" r="6" fill="#89C4D4" fillOpacity="0.4"/>
        <circle cx="82" cy="42" r="5" fill="#89C4D4" fillOpacity="0.35"/>
        {/* legend */}
        <circle cx="22" cy="70" r="5" fill="#C4A882" fillOpacity="0.6"/>
        <text x="32" y="74" fontFamily="system-ui" fontSize="8.5" fill="#4A4440">You</text>
        <circle cx="66" cy="70" r="5" fill="#89C4D4" fillOpacity="0.6"/>
        <text x="76" y="74" fontFamily="system-ui" fontSize="8.5" fill="#4A4440">Niki S.</text>
        {/* overlap stat */}
        <rect x="162" y="28" width="148" height="42" rx="9" fill="#fff" stroke="#EDE8DF" strokeWidth="1.2"/>
        <text x="236" y="48" fontFamily="Georgia,serif" fontSize="22" fill="#2A2520" textAnchor="middle">84%</text>
        <text x="236" y="62" fontFamily="system-ui" fontSize="9" fill="#A09890" textAnchor="middle">scent overlap</text>
      </Ill>
    ),
  },
  {
    color: '#C4A882',
    icon: '♦',
    label: 'ScentdMatch',
    blurb: 'Fragrances you both love — and each other\'s top picks.',
    preview: (
      <Ill viewBox="0 0 320 80" h={80}>
        <rect width="320" height="80" fill="#F8F4EF" rx="10"/>
        <text x="14" y="20" fontFamily="system-ui" fontSize="8" fill="#A09890" letterSpacing="0.12em">SCENTED MATCH</text>
        {[
          { y: 26, name: 'Baccarat Rouge 540', shared: true  },
          { y: 48, name: 'Oud Ispahan',        shared: true  },
        ].map(({ y, name, shared }) => (
          <g key={name}>
            <rect x="12" y={y} width="296" height="18" rx="6" fill={shared ? '#C4A882' : '#EDE8DF'} fillOpacity={shared ? 0.15 : 0.5}/>
            <circle cx="24" cy={y + 9} r="4" fill={shared ? '#C4A882' : '#A09890'}/>
            <text x="34" y={y + 12.5} fontFamily="Georgia,serif" fontSize="10" fill="#2A2520">{name}</text>
            {shared && <text x="286" y={y + 12.5} fontFamily="system-ui" fontSize="9" fill="#8B5A2A">✦</text>}
          </g>
        ))}
      </Ill>
    ),
  },
];

// ─── Expandable feature list ──────────────────────────────────────────────────
const FeatureList = ({ features }: { features: Array<{ color: string; icon: string; label: string; blurb: string; preview: React.ReactNode }> }) => {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
      {features.map((f, i) => {
        const isOpen = open === i;
        return (
          <div key={i} onClick={() => setOpen(isOpen ? null : i)}
            style={{ background: '#fff', border: `1.5px solid ${isOpen ? f.color : '#EDE8DF'}`, borderRadius: 14, overflow: 'hidden', cursor: 'pointer', transition: 'border-color 0.2s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px' }}>
              {/* icon circle */}
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: f.color + '22', border: `1.5px solid ${f.color}55`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: f.icon.length > 2 ? 'system-ui' : 'Georgia, serif', fontSize: f.icon.length > 2 ? 9 : 14, fontWeight: 700, color: f.color }}>
                {f.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'system-ui', fontSize: 12, fontWeight: 600, color: '#2A2520', marginBottom: 2 }}>{f.label}</div>
                <div style={{ fontFamily: 'system-ui', fontSize: 10.5, color: '#8A7E78', lineHeight: 1.4 }}>{f.blurb}</div>
              </div>
              <div style={{ color: f.color, fontSize: 11, flexShrink: 0, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</div>
            </div>
            {isOpen && (
              <div style={{ borderTop: `1px solid ${f.color}33`, background: '#F8F4EF', padding: '12px 14px' }}>
                {f.preview}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Home tab card ────────────────────────────────────────────────────────────
const TabCard = ({ label, hint, color, icon, done, onClick }: { label: string; hint: string; color: string; icon: React.ReactNode; done: boolean; onClick: () => void }) => (
  <div onClick={onClick} style={{ background: done ? color + '0D' : '#fff', border: `1.5px solid ${done ? color : '#EDE8DF'}`, borderRadius: 16, padding: '18px 14px 14px', cursor: 'pointer', position: 'relative' }}>
    {done && (
      <div style={{ position: 'absolute', top: 10, right: 10, width: 18, height: 18, borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', fontFamily: 'system-ui', fontWeight: 700 }}>✓</div>
    )}
    <div style={{ marginBottom: 10 }}>{icon}</div>
    <div style={{ fontFamily: 'system-ui', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#2A2520', marginBottom: 4 }}>{label}</div>
    <div style={{ fontFamily: 'system-ui', fontSize: 10.5, color: '#8A7E78', lineHeight: 1.4 }}>{hint}</div>
  </div>
);

// ─── Back button ──────────────────────────────────────────────────────────────
const Back = ({ onClick }: { onClick: () => void }) => (
  <button onClick={onClick} style={{ fontFamily: 'system-ui', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#A09890', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 20, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
    ← Back
  </button>
);

// ─── Section header ───────────────────────────────────────────────────────────
const SectionHead = ({ title, sub, color }: { title: string; sub: string; color: string }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <div style={{ width: 4, height: 24, borderRadius: 2, background: color }}/>
      <h2 style={{ fontFamily: 'Georgia, serif', fontSize: 22, color: '#2A2520', margin: 0 }}>{title}</h2>
    </div>
    <p style={{ fontFamily: 'system-ui', fontSize: 11.5, color: '#8A7E78', lineHeight: 1.5, margin: 0 }}>{sub}</p>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const WelcomeGuide: React.FC<WelcomeGuideProps> = ({ onClose }) => {
  const [screen, setScreen] = useState<Screen>('home');
  const [done, setDone] = useState<Set<Screen>>(new Set());

  const markDone = (s: Screen) => { setDone(prev => new Set(Array.from(prev).concat(s))); setScreen('home'); };

  const navIcon = (d: string, color = '#6A5E58') => (
    <svg viewBox="0 0 24 24" style={{ width: 24, height: 24, stroke: color, fill: 'none', strokeWidth: 1.8, strokeLinecap: 'round' as const }}>
      <path d={d}/>
    </svg>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(42,36,32,0.65)', padding: '0 0 0 0' }}>
      <div style={{ width: '100%', maxWidth: 430, background: '#F5F0E8', borderRadius: '20px 20px 0 0', maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* color bar */}
        <div style={{ height: 3, background: `linear-gradient(90deg, ${C.discover}, ${C.journal}, ${C.myscent}, ${C.follow})`, flexShrink: 0 }}/>

        <div style={{ overflowY: 'auto', flex: 1, padding: '28px 22px 24px' }}>

          {/* ── HOME ─────────────────────────────────────────────────────── */}
          {screen === 'home' && (
            <>
              <h2 style={{ fontFamily: 'Georgia, serif', fontSize: 28, color: '#2A2520', margin: '0 0 6px' }}>Welcome to Scentd.</h2>
              <p style={{ fontFamily: 'system-ui', fontSize: 12, color: '#8A7E78', lineHeight: 1.5, margin: '0 0 22px' }}>Tap a section to explore — or dive straight in.</p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <TabCard label="Discover" hint="Browse & match fragrances" color={C.discover} done={done.has('discover')} onClick={() => setScreen('discover')}
                  icon={<svg viewBox="0 0 24 24" style={{ width: 26, height: 26, stroke: C.discover, fill: 'none', strokeWidth: 1.8, strokeLinecap: 'round' }}><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="22" y2="22"/></svg>}/>
                <TabCard label="Journal" hint="Log wear sessions & ratings" color={C.journal} done={done.has('journal')} onClick={() => setScreen('journal')}
                  icon={<svg viewBox="0 0 24 24" style={{ width: 26, height: 26, stroke: C.journal, fill: 'none', strokeWidth: 1.8, strokeLinecap: 'round' }}><rect x="4" y="4" width="16" height="16" rx="2"/><line x1="8" y1="9" x2="16" y2="9"/><line x1="8" y1="13" x2="13" y2="13"/></svg>}/>
                <TabCard label="My Scent" hint="Your identity, wishlist & tray" color={C.myscent} done={done.has('myscent')} onClick={() => setScreen('myscent')}
                  icon={<svg viewBox="0 0 24 24" style={{ width: 26, height: 26, stroke: C.myscent, fill: 'none', strokeWidth: 1.8, strokeLinecap: 'round' }}><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>}/>
                <TabCard label="Follow" hint="Connect & compare taste maps" color={C.follow} done={done.has('follow')} onClick={() => setScreen('follow')}
                  icon={<svg viewBox="0 0 24 24" style={{ width: 26, height: 26, stroke: C.follow, fill: 'none', strokeWidth: 1.8, strokeLinecap: 'round' }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}/>
              </div>

              {/* Concierge full-width */}
              <div onClick={() => setScreen('concierge')} style={{ background: done.has('concierge') ? '#2A252008' : '#fff', border: `1.5px solid ${done.has('concierge') ? '#2A2520' : '#EDE8DF'}`, borderRadius: 16, padding: '16px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16, marginBottom: 22, position: 'relative' }}>
                {done.has('concierge') && <div style={{ position: 'absolute', top: 10, right: 10, width: 18, height: 18, borderRadius: '50%', background: '#2A2520', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', fontFamily: 'system-ui' }}>✓</div>}
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#2A2520', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontFamily: 'Georgia, serif', fontSize: 18, color: '#C4A882' }}>✦</span>
                </div>
                <div>
                  <div style={{ fontFamily: 'system-ui', fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#2A2520', marginBottom: 3 }}>Scentd Concierge</div>
                  <div style={{ fontFamily: 'system-ui', fontSize: 11, color: '#8A7E78', lineHeight: 1.4 }}>Your AI advisor — how it learns, what it builds, how to earn more</div>
                </div>
              </div>

              <button onClick={onClose} style={{ width: '100%', background: '#2A2520', color: '#F5F0E8', fontFamily: 'system-ui', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '14px', borderRadius: 12, border: 'none', cursor: 'pointer', marginBottom: 8 }}>Start exploring</button>
              <button onClick={onClose} style={{ width: '100%', background: 'none', border: 'none', fontFamily: 'system-ui', fontSize: 11, color: '#A09890', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '10px', cursor: 'pointer' }}>Skip for now</button>
            </>
          )}

          {/* ── DISCOVER ─────────────────────────────────────────────────── */}
          {screen === 'discover' && (
            <>
              <Back onClick={() => setScreen('home')}/>
              <SectionHead title="Discover" sub="1,500+ fragrances. Your match score on every card. Tap a row to see how it works." color={C.discover}/>
              <FeatureList features={DISCOVER_FEATURES}/>
              <button onClick={() => markDone('discover')} style={{ width: '100%', background: C.discover, color: '#fff', fontFamily: 'system-ui', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '14px', borderRadius: 12, border: 'none', cursor: 'pointer' }}>Got it</button>
            </>
          )}

          {/* ── JOURNAL ──────────────────────────────────────────────────── */}
          {screen === 'journal' && (
            <>
              <Back onClick={() => setScreen('home')}/>
              <SectionHead title="Journal" sub="Every fragrance you've worn. Log each session separately — your history, your words." color={C.journal}/>
              <FeatureList features={JOURNAL_FEATURES}/>
              <button onClick={() => markDone('journal')} style={{ width: '100%', background: C.journal, color: '#fff', fontFamily: 'system-ui', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '14px', borderRadius: 12, border: 'none', cursor: 'pointer' }}>Got it</button>
            </>
          )}

          {/* ── MY SCENT ─────────────────────────────────────────────────── */}
          {screen === 'myscent' && (
            <>
              <Back onClick={() => setScreen('home')}/>
              <SectionHead title="My Scent" sub="Three tabs: your Scent Identity, Wishlist, and Scent Tray." color={C.myscent}/>
              <FeatureList features={MYSCENT_FEATURES}/>
              <button onClick={() => markDone('myscent')} style={{ width: '100%', background: C.myscent, color: '#fff', fontFamily: 'system-ui', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '14px', borderRadius: 12, border: 'none', cursor: 'pointer' }}>Got it</button>
            </>
          )}

          {/* ── FOLLOW ───────────────────────────────────────────────────── */}
          {screen === 'follow' && (
            <>
              <Back onClick={() => setScreen('home')}/>
              <SectionHead title="Follow" sub="Connect with other members. Compare taste maps and see where your noses align." color={C.follow}/>
              <FeatureList features={FOLLOW_FEATURES}/>
              <button onClick={() => markDone('follow')} style={{ width: '100%', background: C.follow, color: '#fff', fontFamily: 'system-ui', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '14px', borderRadius: 12, border: 'none', cursor: 'pointer' }}>Got it</button>
            </>
          )}

          {/* ── CONCIERGE ────────────────────────────────────────────────── */}
          {screen === 'concierge' && (
            <>
              <Back onClick={() => setScreen('home')}/>
              <SectionHead title="Scentd Concierge" sub="Your AI fragrance advisor. Gets smarter the more you journal." color="#2A2520"/>

              {/* Refresh economy */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
                {[
                  { n: '2',   label: 'free on join',             color: C.myscent  },
                  { n: '+1',  label: 'every 10 wear sessions',   color: C.journal  },
                  { n: '+1',  label: 'every 10 contributions',   color: C.discover },
                ].map(({ n, label, color }) => (
                  <div key={label} style={{ background: '#fff', border: `1.5px solid ${color}55`, borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'Georgia, serif', fontSize: 22, color, marginBottom: 4 }}>{n}</div>
                    <div style={{ fontFamily: 'system-ui', fontSize: 9, color: '#8A7E78', lineHeight: 1.3 }}>{label}</div>
                  </div>
                ))}
              </div>

              {/* Feature rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
                {[
                  { icon: '✦', color: C.discover, label: 'Add a fragrance — free', detail: 'Not in the catalog? Concierge finds it, adds it, and credits you. No refresh needed.' },
                  { icon: 'id', color: C.myscent,  label: 'Scent Identity — 1 refresh', detail: 'Reads your entire journal. Writes your archetype, 3 recommendations with match %, and a challenge fragrance.' },
                  { icon: '↻', color: C.journal,   label: 'Auto-refresh every 10 sessions', detail: 'Your profile updates automatically as your journal grows — no action needed.' },
                  { icon: '✎', color: '#C4A882',   label: 'Smarter as you write', detail: 'The more you describe how a scent develops on your skin, the more precisely it scores new fragrances for you.' },
                ].map(r => (
                  <div key={r.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, background: '#fff', border: '1.5px solid #EDE8DF', borderRadius: 12, padding: '13px 14px' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: r.color + '22', border: `1.5px solid ${r.color}55`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: r.icon.length > 2 ? 'system-ui' : 'Georgia, serif', fontSize: r.icon.length > 2 ? 9 : 14, fontWeight: 700, color: r.color, textAlign: 'center' as const, lineHeight: 1.1 }}>{r.icon}</div>
                    <div>
                      <div style={{ fontFamily: 'system-ui', fontSize: 12, fontWeight: 600, color: '#2A2520', marginBottom: 3 }}>{r.label}</div>
                      <div style={{ fontFamily: 'system-ui', fontSize: 11, color: '#8A7E78', lineHeight: 1.4 }}>{r.detail}</div>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={() => markDone('concierge')} style={{ width: '100%', background: '#2A2520', color: '#F5F0E8', fontFamily: 'system-ui', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '14px', borderRadius: 12, border: 'none', cursor: 'pointer' }}>Got it</button>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default WelcomeGuide;
